import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type KeyboardEvent,
} from "react";
import { ChevronDown, Search } from "lucide-react";
import { useFieldId } from "../useFieldId";
import { mergeRefs } from "../overlay";
import { cn } from "@/lib/utils";
import Label from "../Label";
import { FieldError, fieldErrorId } from "../FieldError";
import { FieldHint, fieldHintId } from "../FieldHint";
import {
  fieldControlClass,
  fieldPopoverClass,
  fieldRadius,
} from "../field-variants";
import { CountryFlag } from "./CountryFlag";
import type { CountryCode } from "./types";
import type { CountryOption } from "./types";

export type CountrySelectProps = Omit<
  ComponentPropsWithRef<"button">,
  "children" | "value" | "onChange" | "type"
> & {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  countries: readonly CountryOption[];
  defaultCountry: CountryCode;
  searchPlaceholderLabel: string;
  noResultsLabel: string;
  searchInputAriaLabel: string;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  name?: string;
};

export function CountrySelect({
  label,
  value,
  onChange,
  countries,
  defaultCountry,
  placeholder,
  searchPlaceholderLabel,
  noResultsLabel,
  searchInputAriaLabel,
  error,
  hint,
  required,
  disabled,
  id,
  name,
  ref,
  onClick,
  onKeyDown,
  className: triggerClassName,
  ...triggerRest
}: CountrySelectProps) {
  const inputId = useFieldId(id, name);
  const resolvedSearchInputId = `${inputId}-search`;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const effectiveValue = value || defaultCountry;

  const filteredCountries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (country) =>
        country.label.toLowerCase().includes(q) ||
        country.value.toLowerCase().includes(q),
    );
  }, [countries, searchQuery]);

  const selectedCountry = countries.find((c) => c.value === effectiveValue);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setHighlightIndex(-1);
    triggerRef.current?.focus();
  }, []);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen((wasOpen) => {
      if (wasOpen) {
        setSearchQuery("");
        setHighlightIndex(-1);
        triggerRef.current?.focus();
        return false;
      }
      const selectedIndex = filteredCountries.findIndex(
        (country) => country.value === effectiveValue,
      );
      setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
      window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return true;
    });
  }, [disabled, effectiveValue, filteredCountries]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    if (!isOpen) return;
    const selectedIndex = filteredCountries.findIndex(
      (country) => country.value === effectiveValue,
    );
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [isOpen, filteredCountries, effectiveValue]);

  const selectHighlighted = useCallback(() => {
    if (highlightIndex < 0 || highlightIndex >= filteredCountries.length) {
      return;
    }
    onChange(filteredCountries[highlightIndex].value);
    closeDropdown();
  }, [closeDropdown, filteredCountries, highlightIndex, onChange]);

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
      return;
    }

    if (filteredCountries.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightIndex((index) =>
        index < filteredCountries.length - 1 ? index + 1 : 0,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((index) =>
        index > 0 ? index - 1 : filteredCountries.length - 1,
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      selectHighlighted();
    }
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      closeDropdown();
      return;
    }

    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        const selectedIndex = filteredCountries.findIndex(
          (country) => country.value === effectiveValue,
        );
        setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
        window.setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
  };

  return (
    <div className="w-full">
      {label ? (
        <Label htmlFor={inputId} spacing="field">
          {label}
          {required ? <span className="text-error-500"> *</span> : null}
        </Label>
      ) : null}
      <div ref={containerRef} className="relative">
        <button
          ref={mergeRefs(triggerRef, ref)}
          type="button"
          id={inputId}
          disabled={disabled}
          onClick={(event) => {
            openDropdown();
            onClick?.(event);
          }}
          onKeyDown={(event) => {
            handleTriggerKeyDown(event);
            onKeyDown?.(event);
          }}
          className={fieldControlClass({
            className: cn(
              "flex items-center gap-2.5 text-left",
              isOpen && "border-primary-500 ring-2 ring-primary-500/20",
              triggerClassName,
            ),
          })}
          {...triggerRest}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={
            error
              ? fieldErrorId(inputId)
              : hint
                ? fieldHintId(inputId)
                : undefined
          }
        >
          <CountryFlag country={selectedCountry?.value} />
          <span className="flex-1">{selectedCountry?.label || placeholder}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-surface-400 transition-transform duration-200",
              isOpen && "rotate-180",
            )}
          />
        </button>

        {isOpen ? (
          <div
            className={fieldPopoverClass("absolute left-0 z-50 mt-1 w-full")}
          >
            <div className="border-b border-surface-200 p-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
                <input
                  ref={searchInputRef}
                  id={resolvedSearchInputId}
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={searchPlaceholderLabel}
                  aria-label={searchInputAriaLabel}
                  className={fieldControlClass({
                    size: "sm",
                    className: cn("pl-9 pr-3", fieldRadius.inner),
                  })}
                />
              </div>
            </div>
            <ul role="listbox" className="max-h-60 overflow-auto py-1">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country, index) => {
                  const isSelected = country.value === effectiveValue;
                  const isHighlighted = index === highlightIndex;
                  return (
                    <li
                      key={country.value}
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-surface-900",
                        isHighlighted && "bg-primary-50",
                        isSelected && !isHighlighted && "bg-primary-50/60",
                        "hover:bg-surface-50",
                      )}
                      onMouseEnter={() => setHighlightIndex(index)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        onChange(country.value);
                        closeDropdown();
                      }}
                    >
                      <CountryFlag country={country.value} />
                      <span>{country.label}</span>
                    </li>
                  );
                })
              ) : (
                <li className="px-3 py-6 text-center text-sm text-surface-500">
                  {noResultsLabel}
                </li>
              )}
            </ul>
          </div>
        ) : null}
      </div>
      {error ? (
        <FieldError id={fieldErrorId(inputId)}>{error}</FieldError>
      ) : null}
      {hint && !error ? (
        <FieldHint id={fieldHintId(inputId)}>{hint}</FieldHint>
      ) : null}
    </div>
  );
}

